import { DateTime, } from "luxon";
import { isTokenIdentifier, isBlockHash, } from "@bracket/chainography";
import { claimWithdrawal } from "@bracket/contractor-next/brktETH";
import { wagmiConfig as config, } from "@wagmiconfig";
import type { BrktTransaction, } from "@bracket/prisma";
import type { BlockHash, } from "@bracket/types";



export declare type ClaimProps = {
  txn: BrktTransaction;
}



export declare type ClaimResult = {
  hash: BlockHash|null;
  error: Error|null;
}


/**
 * Claims a withdrawal transaction on the blockchain.
 * 
 * @param {ClaimProps} props - The claim properties
 * @param {BrktTransaction} props.txn - The transaction to claim
 * @returns {Promise<ClaimResult>} The result of the claim operation
 * @throws {Error} If the transaction data is invalid
 * 
 * @example
 * const result = await claim({ txn: withdrawalTransaction });
 * if (result.hash) {
 *   // Claim successful
 * } else {
 *   // Handle error
 * }
 */
export const claim = async ({ txn, }: ClaimProps): Promise<ClaimResult> => {

  try {

    const {
      token,
      amount,
      salt,
      scheduled_withdraw_time,
    } = txn;

    if(typeof scheduled_withdraw_time !== "string")
      throw new Error("invalid scheduled withdrawal time");

    if(!isTokenIdentifier(token))
      throw new Error("invalid token");

    if(typeof amount !== "string")
      throw new Error("amount should be in its stringified form");

    if(typeof salt !== "string" || !salt.length)
      throw new Error("invalid salt");

    const timestamp = DateTime.fromISO(scheduled_withdraw_time).toJSDate();

    // flying blind with a bug which suggests we have a float being converted to a bigint, this amount is Decimal78 (should now be a WEIString) but just in case
    // we have a floated value in this wierd AF type, we're going to strip it.
    const bAmount = BigInt((amount as string).split(".")[0]);

    const hash = await claimWithdrawal({
      token,
      // amount: BigInt(amount),
      amount: bAmount,
      salt_bytes: salt as string,
      timestamp,
      config,
    })
    .catch(err => {
      console.warn(
        `@bracket/platform/client/store/slices/swap/resolvers/claim has failed - ${err instanceof Error ? err.message : "unknown error"}`,
      );
      return null;
    });
  

    if(isBlockHash(hash)) {
      return {
        hash,
        error: null,
      };
    }

    return {
      hash: null,
      error: new Error("mint.resolvers.mint has failed"),
    };
  }
  catch(err) {

    return {
      hash: null,
      error: err instanceof Error ? err : new Error("unknown error"),
    }

  }

}
